import React, { useState } from 'react';
import { Paper, Typography, Button, Box } from '@mui/material';
import {
  white,
  red50,
  gray40,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
} from '../../constants/colors';
import { useNavigate } from 'react-router-dom';
import CustomTextfield from './CustomTextfield';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { reRegisterPasswordSchema } from '../../validation/user';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, passwordSendSettings } from '../../api/firebase/config';
import CustomSnackbar from './CustomSnackbar';
import { authError, common } from '../../modules/error';

const styles = {
  paper: {
    padding: '40px 40px',
    maxWidth: '398px',
    width: '100%',
    backgroundColor: white,
    border: `1px solid ${gray40}`,
    textAlign: 'center',
    '@media (max-width: 600px)': {
      margin: '0 16px',
      padding: '32px 16px',
    },
  },
  beforeButton: {
    height: '43px',
    marginTop: '16px',
    width: '100%',
  },
  mailSendButton: {
    height: '43px',
    marginTop: '16px',
    width: '100%',
    fontWeight: 700,
    '@media (max-width: 340px)': {
      fontSize: '12px',
    },
  },
  titleText: {
    textAlign: 'left',
    fontSize: '22px',
    fontWeight: '400',
    marginBottom: '8px',
    whiteSpace: 'pre-wrap',
    '@media (max-width: 600px)': {
      fontSize: '20px',
    },
  },
  messageText: {
    textAlign: 'left',
    fontSize: '16px',
    whiteSpace: 'pre-wrap',
    marginBottom: '16px',
    '@media (max-width: 340px)': {
      fontSize: '14px',
      marginBottom: '32px',
    },
  },
  errorText: {
    color: red50,
    fontSize: '12px',
    textAlign: 'left',
    marginTop: '4px',
  },
};

const ForgetPasswordCard: React.FC = () => {
  const navigate = useNavigate();
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string>('');

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(reRegisterPasswordSchema),
  });

  const onSubmit = async (): Promise<void> => {
    try {
      // ログイン前は users を参照できないため、ユーザーの存在確認は Auth のエラー（auth/user-not-found）で行う
      await sendPasswordResetEmail(auth, watch('email'), passwordSendSettings());
      await navigate('/auth/password_reset/complete');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      switch (error.code) {
        case 'auth/too-many-requests':
          setSnackbarText(authError.tooManyRequests);
          break;
        case 'auth/invalid-email':
          setSnackbarText(authError.invalidEmail);
          break;
        case 'auth/user-not-found':
        case 'auth/invalid-uid':
          setSnackbarText(authError.userNotFound);
          break;
        default:
          setSnackbarText(common);
      }
      setSnackbarOpen(true);
    }
  };

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return watch('email') === undefined || '' || Boolean(errors.email);
    }
    return false;
  };

  return (
    <>
      <Paper elevation={0} sx={styles.paper}>
        <Typography variant="body2" gutterBottom sx={styles.titleText}>
          パスワード再設定
        </Typography>
        <Typography variant="body2" sx={styles.messageText}>
          メールアドレスを入力後、認証を行います。
        </Typography>
        <Controller
          render={({ field }): JSX.Element => (
            <CustomTextfield
              {...field}
              error={Boolean(errors.email)}
              formMb="24px"
              helperText={errors.email?.message}
              labelText="メールアドレス"
              placeholder="xxx@cto-hands.co.jp"
              requiredText="必須"
              width="396px"
            />
          )}
          control={control}
          name="email"
        />
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Button
            onClick={(): void => {
              navigate('/');
            }}
            variant="outlined"
            sx={[secondaryButtonBackgroundColor, styles.beforeButton]}
          >
            戻る
          </Button>
          <Button
            disabled={buttonDisabledFlag()}
            onClick={async (): Promise<void> => {
              setFirstSubmit(true);
              await handleSubmit(async () => onSubmit())();
            }}
            variant="contained"
            sx={[primaryButtonBackgroundColor, styles.mailSendButton]}
          >
            メール送信
          </Button>
        </Box>
      </Paper>
      <CustomSnackbar
        autoHideDuration={6000}
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={snackbarText}
      />
    </>
  );
};

export default ForgetPasswordCard;
